import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Download, X, Loader2 } from 'lucide-react';

interface ExportModalProps {
  linkId: string;
  requestingUserId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ExportModal({ linkId, requestingUserId, isOpen, onClose }: ExportModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const threeMonthsAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(threeMonthsAgo);
  const [endDate, setEndDate] = useState(today);
  const [caseNumber, setCaseNumber] = useState('');
  const [format, setFormat] = useState<'text' | 'pdf'>('text');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const fn = httpsCallable<Record<string, unknown>, { data: string; filename: string; contentType: string }>(
        getFunctions(getApp()),
        'exportCoParentingThread'
      );
      const result = await fn({ linkId, startDate, endDate, caseNumber: caseNumber || undefined, requestingUserId, format });
      const { data: base64, filename, contentType } = result.data;

      // Decode base64 and trigger download
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-slate-900 dark:text-white">Export Thread</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Case Number (optional)</label>
          <input
            type="text"
            placeholder="e.g. 2025-FC-12345"
            value={caseNumber}
            onChange={e => setCaseNumber(e.target.value)}
            className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Format</label>
          <div className="flex gap-2">
            {(['text', 'pdf'] as const).map(f => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all ${
                  format === f
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                }`}
              >
                {f === 'text' ? 'Plain Text' : 'PDF'}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-rose-500 text-sm font-bold">{error}</p>}

        <button
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
          className="w-full py-3 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-[#4a74e8] transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {loading ? 'Generating...' : 'Generate Export'}
        </button>
      </div>
    </div>
  );
}
